import React from 'react';

import { useProfileContext } from 'Contexts/profileContext';
import { useTextContext } from 'Contexts/textContext';
import Plan from 'Models/plan';

// Helpers
import {
  renderDeductible,
  renderOopMax,
  renderOutNetworkDeductible,
  renderOutNetworkOopMax,
} from './_shared';
import RenderProvidersComponent from './_shared/RenderProvidersComponent';
import { CompareTable } from '../_shared/CompareTable';
import { CompareTableRow } from '../_shared/CompareTableRow';

interface DeductiblesLimitsProps {
  plans: Plan[];
  showCompareRows: boolean;
}

export const DeductiblesLimits = ({ plans, showCompareRows }: DeductiblesLimitsProps) => {
  const { retrieveContentfulData } = useTextContext();
  const { selectedPublicationInfo } = useProfileContext();

  if (!plans.length) {
    return null; // Avoid rendering if no plans exist
  }

  const docFinderEnabled = selectedPublicationInfo?.publication?.docfinder_enabled;

  const firstPlan = plans[0] || null;
  const secondPlan = plans[1] || null;
  const thirdPlan = plans[2] || null;

  // HEADER AND ITEM TEXT
  const inNetworkTitle = retrieveContentfulData<string>(
    'health_section.compare_plan_text.compareModal.inNetwork',
  );
  const outOfNetworkTitle = retrieveContentfulData<string>(
    'health_section.compare_plan_text.compareModal.outOfNetwork',
  );
  const deductibleTitle = retrieveContentfulData<string>(
    'health_section.compare_plan_text.compareModal.deductible',
  );
  const oopLimitTitle = retrieveContentfulData<string>(
    'health_section.compare_plan_text.compareModal.oopLimit',
  );

  const doctorsProvidersTitle = retrieveContentfulData<string>(
    'health_details_section.plan_details.doctors_providers_card_title',
  );

  // ITEM TOOLTIPS
  const inNetworkDeductibleTooltip = retrieveContentfulData<string>('tool_tips.in_network_deductible.text');
  const inNetworkOopTooltip = retrieveContentfulData<string>('tool_tips.in_network_out_of_pocket_limit.text');
  const outNetworkDeductibleTooltip = retrieveContentfulData<string>(
    'tool_tips.out_of_network_deductible.text',
  );
  const outNetworkOopTooltip = retrieveContentfulData<string>(
    'tool_tips.out_of_network_out_of_pocket_limit.text',
  );

  if (!firstPlan) {
    return null; // Ensure we don't proceed if the first plan is missing
  }

  return (
    <>
      <CompareTable tableHeader={inNetworkTitle}>
        <CompareTableRow
          rowHeader={deductibleTitle}
          rowHeaderTooltip={inNetworkDeductibleTooltip}
          columnOne={firstPlan && renderDeductible(firstPlan)}
          columnTwo={secondPlan && renderDeductible(secondPlan)}
          columnThree={thirdPlan && renderDeductible(thirdPlan)}
          keyHint="inn-deductible"
        />

        <CompareTableRow
          rowHeader={oopLimitTitle}
          rowHeaderTooltip={inNetworkOopTooltip}
          columnOne={firstPlan && renderOopMax(firstPlan)}
          columnTwo={secondPlan && renderOopMax(secondPlan)}
          columnThree={thirdPlan && renderOopMax(thirdPlan)}
          keyHint="inn-moop"
        />

        {/* Doctors & Providers */}
        {showCompareRows && docFinderEnabled && (
          <div data-testid="compare-in-provider">
            <CompareTableRow
              rowHeader={doctorsProvidersTitle}
              columnOne={firstPlan && <RenderProvidersComponent plan={firstPlan} inNetwork />}
              columnTwo={secondPlan && <RenderProvidersComponent plan={secondPlan} inNetwork />}
              columnThree={thirdPlan && <RenderProvidersComponent plan={thirdPlan} inNetwork />}
              hideTooltip
              keyHint="inn-doctors-providers"
            />
          </div>
        )}
      </CompareTable>

      {/* OUT-OF-NETWORK */}
      <CompareTable tableHeader={outOfNetworkTitle}>
        {/* OUT-OF-NETWORK DEDUCTIBLE */}
        <CompareTableRow
          rowHeader={deductibleTitle}
          rowHeaderTooltip={outNetworkDeductibleTooltip}
          columnOne={firstPlan && renderOutNetworkDeductible(firstPlan)}
          columnTwo={secondPlan && renderOutNetworkDeductible(secondPlan)}
          columnThree={thirdPlan && renderOutNetworkDeductible(thirdPlan)}
          keyHint="oon-deductible"
        />

        {/* OUT-OF-NETWORK OOP MAX */}
        <CompareTableRow
          rowHeader={oopLimitTitle}
          rowHeaderTooltip={outNetworkOopTooltip}
          columnOne={firstPlan && renderOutNetworkOopMax(firstPlan)}
          columnTwo={secondPlan && renderOutNetworkOopMax(secondPlan)}
          columnThree={thirdPlan && renderOutNetworkOopMax(thirdPlan)}
          keyHint="oon-moop"
        />

        {/* Doctors & Providers */}
        {showCompareRows && docFinderEnabled && (
          <div data-testid="compare-out-provider">
            <CompareTableRow
              rowHeader={doctorsProvidersTitle}
              columnOne={firstPlan && <RenderProvidersComponent plan={firstPlan} inNetwork={false} />}
              columnTwo={secondPlan && <RenderProvidersComponent plan={secondPlan} inNetwork={false} />}
              columnThree={thirdPlan && <RenderProvidersComponent plan={thirdPlan} inNetwork={false} />}
              hideTooltip
              keyHint="oon-doctors-providers"
            />
          </div>
        )}
      </CompareTable>
    </>
  );
};
